import React from 'react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Building2, Edit2, Trash2, CheckCircle } from 'lucide-react';

const ClientTable = ({ 
  clients, 
  selectedClients, 
  selectAll,
  onSelectAll,
  onSelectClient, 
  onEdit, 
  onDelete,
  onActivate
}) => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full" data-testid="clients-table">
        <thead className="bg-slate-50">
          <tr>
            <th className="p-3 w-12">
              <Checkbox
                checked={selectAll}
                onCheckedChange={onSelectAll}
                data-testid="select-all-clients"
              />
            </th>
            <th className="p-3 text-left text-sm font-medium text-slate-600">Client Name</th>
            <th className="p-3 text-left text-sm font-medium text-slate-600">PAN</th>
            <th className="p-3 text-left text-sm font-medium text-slate-600">Contact</th>
            <th className="p-3 text-left text-sm font-medium text-slate-600">Tally Company</th>
            <th className="p-3 text-left text-sm font-medium text-slate-600">Status</th>
            <th className="p-3 text-right text-sm font-medium text-slate-600">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {clients.map(client => {
            const isSelected = selectedClients.has(client.id);
            return (
              <tr 
                key={client.id} 
                className={`hover:bg-slate-50 transition-colors ${isSelected ? 'bg-emerald-50/50' : ''}`}
                data-testid={`client-row-${client.id}`}
              >
                <td className="p-3">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => onSelectClient(client.id)}
                    data-testid={`client-checkbox-${client.id}`}
                  />
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium">{client.name}</p>
                      {client.short_name && (
                        <p className="text-xs text-slate-500">{client.short_name}</p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="p-3">
                  {client.pan ? (
                    <Badge variant="outline" className="font-mono text-xs">{client.pan}</Badge>
                  ) : (
                    <span className="text-slate-400 text-sm">-</span>
                  )}
                </td>
                <td className="p-3">
                  <div className="text-sm">
                    {client.contact_person && <p>{client.contact_person}</p>}
                    {client.phone && <p className="text-slate-500 text-xs">{client.phone}</p>}
                  </div>
                </td>
                <td className="p-3">
                  {client.tally_company ? (
                    <span className="text-sm">{client.tally_company}</span>
                  ) : (
                    <span className="text-slate-400 text-sm">Not configured</span>
                  )}
                </td>
                <td className="p-3">
                  <Badge variant={client.is_active ? "default" : "secondary"}>
                    {client.is_active ? "Active" : "Inactive"}
                  </Badge>
                </td>
                <td className="p-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => onActivate(client)}
                      title="Set as active client"
                      data-testid={`activate-client-${client.id}`}
                    >
                      <CheckCircle className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => onEdit(client)}
                      data-testid={`edit-client-${client.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => onDelete(client.id)}
                      data-testid={`delete-client-${client.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ClientTable;
